/**
 * Names for ids (jorigin/brewlytics#2301): `POST api/names`.
 *
 * A page that shows models, functionals or people by id names them here, not
 * with `GET api/<type>/{id}`: that load fires a READ event, which the server
 * writes as a READ audit row, so naming a page of Audit Search's rows wrote
 * rows, as the viewer, into the logs being reviewed. This endpoint reads each
 * kind once, label only, and writes no row.
 *
 * The answer names only ids the caller may read (models by the listings'
 * rule; functionals and profiles by their READ rule, which every signed-in
 * user holds). Any other id is simply absent: show it as an id.
 *
 * At most 200 ids of a kind go in one request; more are split across as few
 * requests as that allows. Anything that is not an id is left out before
 * asking, as the server would refuse the whole request for it.
 */
import apiClient from './client';
import { QUERY_ID_PATTERN } from '@/types/modelQuery';

export type NameKind = 'models' | 'functionals' | 'profiles';

export const NAME_KINDS: readonly NameKind[] = ['models', 'functionals', 'profiles'];

/** The most ids of one kind the server takes in one request. */
export const MAX_NAME_IDS = 200;

export type NamesRequest = Partial<Record<NameKind, readonly string[]>>;

/** By kind, id (lower-case) to name. */
export type Names = Record<NameKind, Record<string, string>>;

export function emptyNames(): Names {
  return { models: {}, functionals: {}, profiles: {} };
}

/** The ids of each kind that are ids, lower-cased, each once, in order. */
export function cleanNamesRequest(request: NamesRequest): Record<NameKind, string[]> {
  const out = { models: [], functionals: [], profiles: [] } as Record<NameKind, string[]>;
  for (const kind of NAME_KINDS) {
    const seen = new Set<string>();
    for (const id of request[kind] ?? []) {
      if (typeof id !== 'string' || !QUERY_ID_PATTERN.test(id)) continue;
      const lid = id.toLowerCase();
      if (!seen.has(lid)) {
        seen.add(lid);
        out[kind].push(lid);
      }
    }
  }
  return out;
}

/** The names of the ids asked for that the caller may read. No ids: no request. */
export async function getNames(request: NamesRequest): Promise<Names> {
  const ids = cleanNamesRequest(request);
  const rounds = Math.max(0, ...NAME_KINDS.map((k) => Math.ceil(ids[k].length / MAX_NAME_IDS)));
  const names = emptyNames();
  if (rounds === 0) return names;

  const bodies = Array.from({ length: rounds }, (_, i) => {
    const body: Partial<Record<NameKind, string[]>> = {};
    for (const kind of NAME_KINDS) {
      const chunk = ids[kind].slice(i * MAX_NAME_IDS, (i + 1) * MAX_NAME_IDS);
      if (chunk.length) body[kind] = chunk;
    }
    return body;
  });

  const answers = await Promise.all(bodies.map((body) => apiClient.post<Partial<Names>>('api/names', body)));
  for (const { data } of answers) {
    for (const kind of NAME_KINDS) Object.assign(names[kind], data?.[kind] ?? {});
  }
  return names;
}
